import { useState, useEffect, useCallback } from 'react'
import { Webhook, RefreshCw, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react'
import { toast } from '../lib/toast.js'
import getActionDeliveries from '../functions/API/getActionDeliveries.js'
import postRetryActionDelivery from '../functions/API/postRetryActionDelivery.js'

// Map a delivery's raw status to a customer-friendly label + pill styling.
export function statusPresentation(delivery) {
  switch (delivery.status) {
    case 'success':
      return { label: `Delivered${delivery.response_status ? ` (${delivery.response_status})` : ''}`, cls: 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-300' };
    case 'failed':
      return { label: `Failed — will retry (attempt ${delivery.attempts})`, cls: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-300' };
    case 'exhausted':
      return { label: 'Failed — gave up', cls: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-300' };
    case 'processing':
      return { label: 'Sending…', cls: 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-300' };
    default:
      return { label: 'Queued', cls: 'bg-gray-50 text-gray-600 ring-gray-500/20 dark:bg-zinc-700 dark:text-gray-300' };
  }
}

export function DeliveryRow({ delivery, onRetry }) {
  const [expanded, setExpanded] = useState(false);
  const { label, cls } = statusPresentation(delivery);
  const canRetry = delivery.status === 'exhausted' || delivery.status === 'failed';

  return (
    <div className="rounded-md border border-gray-200 dark:border-zinc-700">
      <div className="flex items-center justify-between gap-2 p-2.5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2 min-w-0">
          <Webhook className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400 shrink-0" />
          <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}>{label}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{delivery.request_url || delivery.event}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {delivery.latency_ms != null && <span className="text-[11px] text-gray-400">{delivery.latency_ms}ms</span>}
          <span className="text-[11px] text-gray-400">{delivery.updated_at ? new Date(delivery.updated_at).toLocaleString() : ''}</span>
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-zinc-700 p-2.5 space-y-2 text-xs">
          <div className="grid grid-cols-2 gap-2 text-gray-600 dark:text-gray-400">
            <div><span className="font-medium">Event:</span> {delivery.event}</div>
            <div><span className="font-medium">Attempts:</span> {delivery.attempts}</div>
            {delivery.response_status != null && <div><span className="font-medium">Response:</span> {delivery.response_status}</div>}
            {delivery.next_attempt_at && delivery.status === 'failed' && (
              <div><span className="font-medium">Next retry:</span> {new Date(delivery.next_attempt_at).toLocaleString()}</div>
            )}
          </div>
          {delivery.error && (
            <div className="rounded bg-red-50 dark:bg-red-900/20 p-2 text-red-700 dark:text-red-300 break-words">{delivery.error}</div>
          )}
          {delivery.response_body && (
            <pre className="rounded bg-gray-100 dark:bg-zinc-900 p-2 text-[11px] text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre-wrap break-words">{delivery.response_body}</pre>
          )}
          {canRetry && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRetry(delivery.id); }}
              className="inline-flex items-center gap-1 rounded-md bg-white dark:bg-zinc-700 px-2 py-1 text-xs font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600"
            >
              <RotateCcw className="h-3 w-3" /> Retry
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Admin-only Action Activity for a task instance. Shows outbound webhook
 * deliveries, updating live while open (deliveries are sent asynchronously).
 * Never rendered in the customer view.
 */
export default function ActionActivity({ taskInstanceId }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getActionDeliveries({ task_instance_id: taskInstanceId });
      if (res.response.ok) setDeliveries(res.data.deliveries || []);
    } catch (e) {
      // Non-fatal.
    } finally {
      setLoaded(true);
    }
  }, [taskInstanceId]);

  useEffect(() => {
    if (!taskInstanceId) return;
    load();
    const interval = setInterval(load, 10000); // mirror the message poll cadence
    return () => clearInterval(interval);
  }, [taskInstanceId, load]);

  const handleRetry = async (id) => {
    try {
      const res = await postRetryActionDelivery(id);
      if (res.response.ok) { toast.success('Delivery queued for retry.'); load(); }
      else toast.error('Could not retry the delivery.');
    } catch (e) {
      toast.error('Could not retry the delivery.');
    }
  }

  if (loaded && deliveries.length === 0) return null; // hide when nothing to show

  return (
    <div className="bg-white dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Webhook className="h-4 w-4 text-sky-600 dark:text-sky-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Action Activity</h3>
        </div>
        <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-700" title="Refresh" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 text-gray-400" />
        </button>
      </div>
      <div className="space-y-1.5">
        {deliveries.map((d) => <DeliveryRow key={d.id} delivery={d} onRetry={handleRetry} />)}
      </div>
    </div>
  )
}
